import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button'
import { useCallback, useState, type ElementType, type FocusEvent, type MouseEvent } from 'react'

import { cn } from './cn'

/** MUI's button props, rendered as `component` (a router `Link`, say) when one is given. */
export type ButtonProps<C extends ElementType = 'button'> = MuiButtonProps<C, { component?: C }> & {
  /**
   * Working, such as saving or loading: `aria-busy` and `aria-disabled`, looking disabled and
   * ignoring presses, but still focusable, so focus stays on a button whose own press started it.
   */
  readonly busy?: boolean | undefined
}

/**
 * The archetype's button: contained by default. Its sizes, colors and states are the theme's
 * (`shared/theme/theme.ts`). An icon goes in as a child, before or after the label.
 *
 * `disabled` would drop focus to the page if it came while the button had focus (WCAG 2.4.3), so
 * until focus leaves, such a button is `aria-disabled` instead and ignores presses.
 */
export function Button<C extends ElementType = 'button'>({
  variant = 'contained',
  busy = false,
  disabled = false,
  className,
  style,
  onClick,
  onFocus,
  onBlur,
  ...props
}: ButtonProps<C>) {
  const [isFocused, setFocused] = useState(false)
  const isInactive = busy || (disabled && isFocused)

  const focus = useCallback(
    (event: FocusEvent<HTMLButtonElement>) => {
      setFocused(true)
      ;(onFocus as ((event: FocusEvent<HTMLButtonElement>) => void) | undefined)?.(event)
    },
    [onFocus],
  )
  const blur = useCallback(
    (event: FocusEvent<HTMLButtonElement>) => {
      setFocused(false)
      ;(onBlur as ((event: FocusEvent<HTMLButtonElement>) => void) | undefined)?.(event)
    },
    [onBlur],
  )
  const click = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // Neither a form's submit, even by Enter in a field, nor a link's navigation runs while it is inactive.
      if (isInactive) {
        event.preventDefault()
        return
      }
      ;(onClick as ((event: MouseEvent<HTMLButtonElement>) => void) | undefined)?.(event)
    },
    [isInactive, onClick],
  )

  return (
    <MuiButton
      variant={variant}
      {...props}
      disabled={disabled && !isInactive}
      // The theme's disabled look, without the attribute; still taking the pointer, so a press is canceled here.
      className={cn(className, isInactive && 'Mui-disabled')}
      style={isInactive ? { ...style, pointerEvents: 'auto' } : style}
      onClick={click}
      onFocus={focus}
      onBlur={blur}
      {...(isInactive && { 'aria-disabled': true })}
      {...(busy && { 'aria-busy': true })}
    />
  )
}
